import type { Adjustments } from "@proof/core";
export type Crop = Adjustments["crop"];
export type CropHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
export type CropPoint = { x: number; y: number };
const clamp = (value: number, min = 0, max = 1) =>
  Math.max(Math.min(min, max), Math.min(max, value));
/** Coordinates always describe the untransformed source image, regardless of preview size. */
export function drawCrop(
  anchor: CropPoint,
  point: CropPoint,
  ratio?: number,
): Crop {
  const a = {
      x: clamp(anchor.x, 0, 0.999999),
      y: clamp(anchor.y, 0, 0.999999),
    },
    b = { x: clamp(point.x), y: clamp(point.y) };
  const dx = b.x >= a.x ? 1 : -1,
    dy = b.y >= a.y ? 1 : -1;
  const maxWidth = dx > 0 ? 1 - a.x : a.x,
    maxHeight = dy > 0 ? 1 - a.y : a.y;
  let width = Math.abs(b.x - a.x),
    height = Math.abs(b.y - a.y);
  if (ratio && Number.isFinite(ratio) && ratio > 0) {
    width = Math.min(
      Math.max(
        width,
        height * ratio,
        Math.min(0.01, maxWidth, maxHeight * ratio),
      ),
      maxWidth,
      maxHeight * ratio,
    );
    height = width / ratio;
  } else {
    width = Math.min(maxWidth, Math.max(0.001, width));
    height = Math.min(maxHeight, Math.max(0.001, height));
  }
  return {
    x: dx > 0 ? a.x : a.x - width,
    y: dy > 0 ? a.y : a.y - height,
    width,
    height,
  };
}
export function moveCrop(crop: Crop, dx: number, dy: number): Crop {
  return {
    ...crop,
    x: clamp(crop.x + dx, 0, 1 - crop.width),
    y: clamp(crop.y + dy, 0, 1 - crop.height),
  };
}
export function resizeCrop(
  crop: Crop,
  handle: CropHandle,
  point: CropPoint,
  ratio?: number,
): Crop {
  const west = handle.includes("w"),
    east = handle.includes("e"),
    north = handle.includes("n"),
    south = handle.includes("s");
  if (west || east) {
    const anchorX = west ? crop.x + crop.width : crop.x;
    const width = west
      ? clamp(anchorX - point.x, 0.001, anchorX)
      : clamp(point.x - anchorX, 0.001, 1 - anchorX);
    if (ratio && !north && !south) {
      const cy = crop.y + crop.height / 2,
        height = Math.min(width / ratio, 2 * Math.min(cy, 1 - cy));
      return {
        x: west ? anchorX - height * ratio : anchorX,
        y: cy - height / 2,
        width: height * ratio,
        height,
      };
    }
    if (north || south)
      return drawCrop(
        { x: anchorX, y: north ? crop.y + crop.height : crop.y },
        {
          x: west ? anchorX - width : anchorX + width,
          y: north
            ? clamp(point.y, 0, crop.y + crop.height - 0.001)
            : clamp(point.y, crop.y + 0.001, 1),
        },
        ratio,
      );
    return { ...crop, x: west ? anchorX - width : anchorX, width };
  }
  const anchorY = north ? crop.y + crop.height : crop.y,
    height = north
      ? clamp(anchorY - point.y, 0.001, anchorY)
      : clamp(point.y - anchorY, 0.001, 1 - anchorY);
  if (ratio) {
    const cx = crop.x + crop.width / 2,
      width = Math.min(height * ratio, 2 * Math.min(cx, 1 - cx));
    return {
      x: cx - width / 2,
      y: north ? anchorY - width / ratio : anchorY,
      width,
      height: width / ratio,
    };
  }
  return { ...crop, y: north ? anchorY - height : anchorY, height };
}
